import styled from "styled-components";
import { Heading } from "../../components/Heading/Heading";
import { Subheading } from "../../components/Subheading/Subheading";
import { Section } from "../../components/Section/Section";
import { Select } from "../../components/Select/Select";

interface ContainerProps extends Section {
  backgroundColor?: string;
  backgroundType?: "color" | "image" | "video" | "none";
}

export const DynamicFormWithGalleryContainer = styled(Section)<ContainerProps>`
  position: relative;
  width: 100%;
  background-color: ${(props) =>
    props.backgroundType === "image" || props.backgroundType === "video"
      ? "transparent"
      : props.backgroundType === "color"
        ? props.backgroundColor || "var(--general-background, #ffffff)"
        : "transparent"};
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1216px;
  margin: 0 auto;
  display: flex;
  gap: 2.5rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    gap: 2rem;
  }
`;

export const GalleryWrapper = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: 769px) {
    flex-basis: 55%;
    position: sticky;
    top: 1.5rem;
  }
`;

interface FormWrapperProps {
  bgColor?: string;
}

export const FormWrapper = styled.div<FormWrapperProps>`
  flex: 1;
  border-style: solid;
  border-width: var(--design-card-border-width, 1px);
  border-color: var(--cards-border, #e2e8f0);
  border-radius: var(--design-card-border-radius, 8px);
  max-width: 490px;
  background-color: ${(props) =>
    props.bgColor || "var(--cards-background, transparent)"};

  @media (min-width: 769px) {
    flex-basis: 45%;
  }
`;

export const TitleSection = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

interface TitleProps {
  color?: string;
}

export const Title = styled(Heading)<TitleProps>`
  color: ${(props) => props.color || "var(--text-title, #0f172a)"};
  margin: 0 0 1rem 0;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

interface SubtitleProps {
  color?: string;
}

export const Subtitle = styled(Subheading)<SubtitleProps>`
  color: ${(props) => props.color || "var(--text-body, #475569)"};
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: initial;

  p {
    margin: 0 0 0.5rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const GallerySectionContainer = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

export const ThumbnailsContainer = styled.div`
  margin-top: 1rem;

  @media (max-width: 768px) {
    margin-top: 0.75rem;
  }
`;

export const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  .image {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    object-fit: cover;
  }

  .desktop-image {
    @media (max-width: 767px) {
      display: none;
    }
  }

  .mobile-image {
    @media (min-width: 768px) {
      display: none;
    }
  }
`;

interface ShadowProps {
  shadowColor?: string;
}

export const Shadow = styled.div<ShadowProps>`
  position: absolute;
  inset: 0;
  z-index: 0;
  background-color: ${({ shadowColor }) =>
    shadowColor || "rgba(0, 0, 0, 0.25)"};
`;

export const SingleImageWrapper = styled.div<{
  $imageAspectRatio?: "fixed" | "auto";
}>`
  width: 100%;
  ${({ $imageAspectRatio }) =>
    $imageAspectRatio !== "auto" ? "aspect-ratio: 3 / 2;" : ""}
  overflow: hidden;

  .single-image {
    width: 100%;
    ${({ $imageAspectRatio }) =>
      $imageAspectRatio !== "auto" ? "height: 100%;" : "height: auto;"}
    object-fit: cover;
  }
`;

export const HighlightSlotsCard = styled.div`
  border: var(--design-card-border-width, 1px) solid
    var(--general-border, #e2e8f0);
  border-radius: var(--design-card-border-radius, 0.75rem);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

export const HighlightSlotsTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
`;

export const HighlightSlot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.375rem;
  min-width: 0;
`;

export const HighlightSlotTitle = styled.p`
  font-family: var(--typography-title-font-family), sans-serif;
  font-weight: var(--typography-title-font-weight, 600);
  font-size: var(--typography-title-font-size-mobile);
  line-height: var(--typography-title-line-height, 1.5);
  letter-spacing: var(--typography-title-letter-spacing, 0);
  color: var(--text-title, #0f172a);
  text-align: center;
  margin: 0;
  width: 100%;

  @media (min-width: 769px) {
    font-size: var(--typography-title-font-size-desktop);
  }
`;

export const HighlightSlotDescription = styled.div`
  font-family: var(--typography-subheading-font-family), sans-serif;
  font-weight: var(--typography-subheading-font-weight, 400);
  font-size: var(--typography-subheading-font-size-mobile);
  line-height: var(--typography-subheading-line-height, 1.5);
  letter-spacing: var(--typography-subheading-letter-spacing, 0);
  color: var(--text-body, #475569);
  text-align: center;
  margin: 0;
  width: 100%;

  @media (min-width: 769px) {
    font-size: var(--typography-subheading-font-size-desktop);
  }
`;

export const HighlightVerticalDivider = styled.div`
  width: 0;
  align-self: stretch;
  border-left: 1px solid var(--general-border, #e2e8f0);
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const HighlightHorizontalDivider = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid var(--general-border, #e2e8f0);
`;

export const HighlightBottomSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.375rem;
  width: 100%;
`;

export const HighlightBottomTitle = styled.p`
  font-family: var(--typography-title-font-family), sans-serif;
  font-weight: var(--typography-title-font-weight, 600);
  font-size: var(--typography-title-font-size-mobile);
  line-height: var(--typography-title-line-height, 1.5);
  letter-spacing: var(--typography-title-letter-spacing, 0);
  color: var(--text-title, #0f172a);
  text-align: center;
  margin: 0;

  @media (min-width: 769px) {
    font-size: var(--typography-title-font-size-desktop);
  }
`;

export const HighlightBottomDescription = styled.div`
  font-family: var(--typography-heading-font-family), sans-serif;
  font-weight: var(--typography-heading-font-weight, 600);
  font-size: var(--typography-heading-font-size-mobile);
  line-height: var(--typography-heading-line-height, 1.2);
  letter-spacing: var(--typography-heading-letter-spacing, 0);
  color: var(--general-primary, #3754e2);
  text-align: center;
  margin: 0;

  @media (min-width: 769px) {
    font-size: var(--typography-heading-font-size-desktop);
  }
`;

export const HighlightSelect = styled(Select)`
  background-color: transparent;
  margin-bottom: 16px;
`;

export const ComplementaryTextWrapper = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 100%;
`;

export const ComplementaryTextDescription = styled.div`
  font-family: var(--typography-text-font-family), sans-serif;
  font-weight: var(--typography-text-font-weight, 400);
  font-size: var(--typography-text-font-size-mobile);
  line-height: var(--typography-text-line-height, 1.5);
  letter-spacing: var(--typography-text-letter-spacing, 0);
  color: var(--text-body, #64748b);
  margin: 0;

  @media (min-width: 769px) {
    font-size: var(--typography-text-font-size-desktop);
  }
`;

export const CountdownWrapper = styled.div<{
  $bg?: string;
  $border?: string;
  $orientation?: "horizontal" | "vertical";
}>`
  display: flex;
  // Orientação = eixo do cabeçalho (ícone + rótulo) em relação às caixas:
  // horizontal = lado a lado (row); vertical = cabeçalho acima das caixas (column).
  // As caixas de número em si ficam SEMPRE em linha (ver CountdownUnits).
  flex-direction: ${(props) =>
    props.$orientation === "horizontal" ? "row" : "column"};
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  background-color: ${(props) => props.$bg || "var(--cards-background, #ffffff)"};
  border: 1px solid ${(props) => props.$border || "var(--cards-border, #e2e8f0)"};
  border-radius: var(--design-card-border-radius, 12px);
`;

// Ícone + "Esta oferta acaba em" na mesma linha, centralizados.
export const CountdownHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  text-align: center;
`;

export const CountdownIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  & svg {
    width: 20px;
    height: 20px;
  }
`;

export const CountdownLabel = styled.span<{ $color?: string }>`
  font-family: var(--typography-text-font-family), sans-serif;
  font-weight: 600;
  font-size: 1rem;
  color: ${(props) => props.$color || "var(--cards-body, #64748b)"};
`;

export const CountdownText = styled.span<{ $color?: string }>`
  font-family: var(--typography-text-font-family), sans-serif;
  font-weight: 700;
  font-size: 1.125rem;
  color: ${(props) => props.$color || "var(--cards-title, #0f172a)"};
`;

// As caixas de número ficam SEMPRE lado a lado (row), independente da orientação —
// o toggle Orientação move o cabeçalho (ver CountdownWrapper), nunca empilha os números.
export const CountdownUnits = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

export const CountdownUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

export const CountdownUnitBox = styled.div<{
  $boxed?: boolean;
  $boxBg?: string;
  $boxText?: string;
  $numbers?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--typography-title-font-family), sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1;
  ${(props) =>
    props.$boxed
      ? `
    min-width: 3rem;
    padding: 0.5rem 0.5rem;
    border-radius: 0.5rem;
    background-color: ${props.$boxBg || "var(--general-primary, #3754e2)"};
    color: ${props.$boxText || "var(--buttons-text, #ffffff)"};
  `
      : `
    background: transparent;
    color: ${props.$numbers || "var(--cards-title, #0f172a)"};
  `}
`;

export const CountdownUnitLabel = styled.span<{ $color?: string }>`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(props) => props.$color || "var(--cards-body, #64748b)"};
`;
